import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import {
  format0,
  format8,
  formatWholeDollarCurrency,
  signedPercent,
} from 'utils/number';
import { fromDateParam } from 'utils/dates';
import type { ComparisonSummaryData } from './types';
import style from './comparison-totals.module.scss';

interface ComparisonTotalsProps {
  summary: ComparisonSummaryData | null;
  isLoading: boolean;
}

const PLACEHOLDER = '--';

/**
 * Total Volume cell: MBOE with its Oil EUR (Mbbl) / Gas EUR (MMcf) streams stacked beneath, so
 * users see the exact oil and gas figures — mirroring each property card's Property Volume
 * breakdown (same tags). MBOE == oil + gas / 20. The unit lives in the row tag only, so each
 * value is a bare, sortable number ("34,000", not "34,000 MBOE"). The BE always sends a number
 * (never null), so a genuine 0 renders "0" — consistent with the oil/gas lines — rather than the
 * "--" placeholder. The Target cell shows the Origin→Target percent change on the MBOE line.
 * @param {object} props - The side's total MBOE, oil (Mbbl), gas (MMcf), and optional MBOE delta
 * @return {JSX.Element} The stacked volume breakdown
 */
const VolumeCell = ({
  mboe,
  oil,
  gas,
  delta = null,
}: {
  mboe: number;
  oil: number;
  gas: number;
  delta?: { text: string; positive: boolean } | null;
}) => (
  <>
    <div className={style.breakdownLine}>
      <span className={style.breakdownTag}>MBOE</span>
      <span>
        {format0(mboe)}
        {delta && (
          <span
            className={
              delta.positive ? style.deltaPositive : style.deltaNegative
            }
          >
            {' '}
            {delta.text}
          </span>
        )}
      </span>
    </div>
    <div className={style.breakdownLine}>
      <span className={style.breakdownTag}>Oil EUR (Mbbl)</span>
      <span>{format0(oil)}</span>
    </div>
    <div className={style.breakdownLine}>
      <span className={style.breakdownTag}>Gas EUR (MMcf)</span>
      <span>{format0(gas)}</span>
    </div>
  </>
);

/**
 * Formats the Origin→Target percent change, or null when it isn't a meaningful delta.
 *
 * Total Package Value is NPV, so a baseline can be negative or the two sides can straddle zero.
 * Two guards keep the chip honest (both are no-ops for the always-non-negative NRI/Volume chips):
 *   - `Math.abs(origin)` as the denominator: the sign of the percent then follows the direction
 *     of change `(target - origin)`, so it always agrees with the green/red `*DeltaPositive`
 *     color. A raw `/ origin` divide flips the sign for a negative baseline — `-$1M → -$0.5M`
 *     is an improvement (green) yet reads "-50%".
 *   - a sign flip across zero (e.g. `-$1M → +$0.5M`) has no meaningful percentage, so it returns
 *     null and only the per-side dollar values render.
 * @param {number} origin - The Origin (side A) value
 * @param {number} target - The Target (side B) value
 * @return {string | null} A signed percent string like "+30.5%", or null
 */
const formatDelta = (origin: number, target: number): string | null => {
  if (!origin) return null;
  const crossesZero = origin < 0 !== target < 0 && target !== 0;
  if (crossesZero) return null;
  return signedPercent(((target - origin) / Math.abs(origin)) * 100);
};

/**
 * "Summary" section: Origin vs Target totals (Total Properties, Total Net
 * Interest) with the Origin→Target percent change on Net Interest. Collapsible,
 * mirroring the Metrics and Comparison-by-Slot sections.
 * @param {ComparisonTotalsProps} props - The comparison summary data and loading flag
 * @return {JSX.Element} The Summary totals section
 */
export const ComparisonTotals = ({
  summary,
  isLoading,
}: ComparisonTotalsProps) => {
  const [expanded, setExpanded] = useState(true);
  const ready = !isLoading && !!summary;

  // Only show the Net Interest delta when the two sides actually differ at the displayed
  // precision — identical values (e.g. 0.06693904 vs 0.06693904) would otherwise render a
  // meaningless "+0.0%". Mirrors numericDelta's display-equality guard (property-attributes).
  const nriDiffers =
    ready && format8(summary.totalNriA) !== format8(summary.totalNriB);
  const nriDelta = nriDiffers
    ? formatDelta(summary.totalNriA, summary.totalNriB)
    : null;
  const deltaPositive = ready ? summary.totalNriB >= summary.totalNriA : true;

  // Total Volume gets the same Origin→Target percent chip as Net Interest — arguably the
  // number users most want the change on. Only shown when the two sides differ at the displayed
  // (whole-MBOE) precision, so identical totals don't render a meaningless "+0.0%".
  const volumeDiffers =
    ready && format0(summary.totalVolumeA) !== format0(summary.totalVolumeB);
  const volumeDelta = volumeDiffers
    ? formatDelta(summary.totalVolumeA, summary.totalVolumeB)
    : null;
  const volumeDeltaPositive = ready
    ? summary.totalVolumeB >= summary.totalVolumeA
    : true;
  // Precomputed like packageValueDelta/nriDelta so the JSX passes a single const (not a fresh
  // inline object literal) to VolumeCell — consistent with the other summary chips.
  const volumeDeltaChip = volumeDelta
    ? { text: volumeDelta, positive: volumeDeltaPositive }
    : null;

  // Total Package Value gets the same Origin→Target percent chip, shown only when the two sides
  // differ at the displayed (whole-dollar) precision so identical totals don't render "+0.0%".
  // Total Package Value is nullable: the BE sends null when a side has no priced slot (a pricing
  // failure or an empty side), which renders "--" (unknown), never $0. So the percent chip shows
  // ONLY when BOTH sides are known — a null side would otherwise drive a fabricated delta.
  const packageValuesKnown =
    ready &&
    summary.totalPackageValueA !== null &&
    summary.totalPackageValueB !== null;
  const packageValueDiffers =
    packageValuesKnown &&
    formatWholeDollarCurrency(summary.totalPackageValueA) !==
      formatWholeDollarCurrency(summary.totalPackageValueB);
  const packageValueDelta =
    packageValueDiffers &&
    summary.totalPackageValueA !== null &&
    summary.totalPackageValueB !== null
      ? formatDelta(summary.totalPackageValueA, summary.totalPackageValueB)
      : null;
  const packageValueDeltaPositive =
    packageValuesKnown &&
    summary.totalPackageValueA !== null &&
    summary.totalPackageValueB !== null &&
    summary.totalPackageValueB >= summary.totalPackageValueA;

  return (
    <Accordion
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      className={style.card}
    >
      <AccordionSummary>
        <h3 className={style.heading}>Summary</h3>
      </AccordionSummary>
      <AccordionDetails>
        <table className={style.table}>
          <thead>
            <tr>
              <th className={style.rowHeader} scope="col">
                Package Evals
              </th>
              <th className={style.value} scope="col">
                Origin
              </th>
              <th className={style.value} scope="col">
                Target
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className={style.rowHeader} scope="row">
                Total Package Value
              </th>
              <td className={style.value}>
                {ready
                  ? formatWholeDollarCurrency(summary.totalPackageValueA)
                  : PLACEHOLDER}
              </td>
              <td className={style.value}>
                {ready
                  ? formatWholeDollarCurrency(summary.totalPackageValueB)
                  : PLACEHOLDER}
                {packageValueDelta && (
                  <span
                    className={
                      packageValueDeltaPositive
                        ? style.deltaPositive
                        : style.deltaNegative
                    }
                  >
                    {' '}
                    {packageValueDelta}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th className={style.rowHeader} scope="row">
                Total Properties
              </th>
              <td className={style.value}>
                {ready ? format0(summary.totalSlotsA) : PLACEHOLDER}
              </td>
              <td className={style.value}>
                {ready ? format0(summary.totalSlotsB) : PLACEHOLDER}
              </td>
            </tr>
            <tr>
              <th className={style.rowHeader} scope="row">
                Total Net Interest
              </th>
              <td className={style.value}>
                {ready ? format8(summary.totalNriA) : PLACEHOLDER}
              </td>
              <td className={style.value}>
                {ready ? format8(summary.totalNriB) : PLACEHOLDER}
                {nriDelta && (
                  <span
                    className={
                      deltaPositive ? style.deltaPositive : style.deltaNegative
                    }
                  >
                    {' '}
                    {nriDelta}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th className={style.rowHeader} scope="row">
                Total Volume
              </th>
              <td className={style.value}>
                {ready ? (
                  <VolumeCell
                    mboe={summary.totalVolumeA}
                    oil={summary.totalOilA}
                    gas={summary.totalGasA}
                  />
                ) : (
                  PLACEHOLDER
                )}
              </td>
              <td className={style.value}>
                {ready ? (
                  <VolumeCell
                    mboe={summary.totalVolumeB}
                    oil={summary.totalOilB}
                    gas={summary.totalGasB}
                    delta={volumeDeltaChip}
                  />
                ) : (
                  PLACEHOLDER
                )}
              </td>
            </tr>
            <tr>
              <th className={style.rowHeader} scope="row">
                Effective Date
              </th>
              <td className={style.value}>
                {ready
                  ? fromDateParam(summary.effectiveDateA) || PLACEHOLDER
                  : PLACEHOLDER}
              </td>
              <td className={style.value}>
                {ready
                  ? fromDateParam(summary.effectiveDateB) || PLACEHOLDER
                  : PLACEHOLDER}
              </td>
            </tr>
          </tbody>
        </table>
      </AccordionDetails>
    </Accordion>
  );
};
